import { Button, Checkbox, ListItemText, MenuItem, Select, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import Swal from "sweetalert2";

import HeaderBread from "../../../../components/HeaderBread";
import { useDataLayerValue } from "../../../../DataLayer/DataLayer";
import Layout from "../../../../Layout";
import CustomHeaderTitle from "../../../../components/constants/CustomHeaderTitle";
import CustomizedTable from "../../../../components/UI/CustomizedTable";
import Logs from "../../../../components/Logs";
import SearchField from "../../../../components/SearchField";
import SwitchBar from "../../../../components/Pages/SwitchBar";
import {
  getShorexSalesReport,
  updateShorexBookingStatus,
  getShorexBookingPackageOptions,
  getShorexVoidReasons,
  getShorexCurrentBusinessDate,
} from "../../../../utils/ApiCalls/BackOffice/Shorex";
import { addlog } from "../../../../utils/ApiCalls/Logs";
import { LOG_ACTIONS, LOG_TYPES } from "../../../../utils/Constant/LogConstant";
import { useDebounce } from "../../../../hooks";

const { RangePicker } = DatePicker;
const PAGE_PATH = "/shore/back-office/shore-excursions/void-transaction";

// excNo/name alone can repeat across different sailing dates (different
// excId) - the date disambiguates them. Kept separate from the base label so
// list rows can show it as a secondary line instead of crowding one line.
const excursionOptionDate = (exc) =>
  exc.startDate ? moment.utc(exc.startDate).format("YYYY-MM-DD") : undefined;
const excursionOptionLabel = (exc) => {
  const date = excursionOptionDate(exc);
  return `${exc.excNo}-${exc.name}${date ? ` (${date})` : ""}`;
};
const VOIDABLE_STATUSES = ["booked"];

const getDefaultDateRange = () =>
  JSON.stringify([moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")]);

const parseDateRange = (range) => {
  if (!range) return null;
  try {
    const [start, end] = JSON.parse(range);
    if (start && end) return [dayjs(start, "YYYY-MM-DD"), dayjs(end, "YYYY-MM-DD")];
  } catch (e) {
    // unparsable - treat as no selection
  }
  return null;
};

const statusChipStyle = (status) => {
  const base = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
  };
  switch ((status || "").toLowerCase()) {
    case "booked":
      return { ...base, background: "#0ab402" };
    case "voided":
      return { ...base, background: "#616161" };
    case "failed":
      return { ...base, background: "#eb6363" };
    case "pending":
      return { ...base, background: "#f5a623" };
    default:
      return { ...base, background: "#9e9e9e" };
  }
};

const formatAmount = (val) => (val != null ? Number(val).toFixed(2) : "-");
const formatStatus = (status) =>
  status ? status.charAt(0).toUpperCase() + status.slice(1) : "-";

const ShorexVoidTransaction = () => {
  const locationP = useLocation();
  const navigate = useNavigate();
  const [{ userDetails }, dispatch] = useDataLayerValue();
  const [topTab, setTopTab] = useState("void");

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Void Transaction", route: PAGE_PATH },
  ];

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [dateRange, setDateRange] = useState(() => {
    const params = new URLSearchParams(locationP.search);
    return params.has("dateRange") ? params.get("dateRange") : getDefaultDateRange();
  });
  const parseIdList = (val) => (val ? val.split(",").filter(Boolean) : []);
  const [statuses, setStatuses] = useState(
    () => parseIdList(new URLSearchParams(locationP.search).get("status"))
  );
  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("pageSize")) || 10
  );
  const [search, setSearch] = useState(
    () => new URLSearchParams(locationP.search).get("search") || ""
  );
  const debouncedSearch = useDebounce(search, 1000);
  const [excIds, setExcIds] = useState(
    () => parseIdList(new URLSearchParams(locationP.search).get("excIds"))
  );
  const [excOptions, setExcOptions] = useState([]);
  const [voidReasons, setVoidReasons] = useState([]);
  const [currentBusinessDate, setCurrentBusinessDate] = useState(null);

  useEffect(() => {
    (async () => {
      const [excRes, voidReasonsRes, businessDateRes] = await Promise.all([
        getShorexBookingPackageOptions(),
        getShorexVoidReasons(),
        getShorexCurrentBusinessDate(),
      ]);
      const sortAlphaNumeric = (a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
      setExcOptions(
        (excRes?.data || [])
          .slice()
          .sort((a, b) => sortAlphaNumeric(excursionOptionLabel(a), excursionOptionLabel(b)))
      );
      setVoidReasons(voidReasonsRes?.data || []);
      setCurrentBusinessDate(businessDateRes?.data?.businessDate || null);
    })();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("pageSize", pageSize);
    if (search) params.set("search", search);
    if (statuses.length) params.set("status", statuses.join(","));
    if (excIds.length) params.set("excIds", excIds.join(","));
    params.set("dateRange", dateRange);
    navigate({ search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, statuses, dateRange, excIds]);

  const columns = [
    {
      title: "Sl",
      key: "sl",
      width: 40,
      render: (val, rec, idx) => (page - 1) * pageSize + idx + 1,
    },
    { title: "Fidelio Business Date", dataIndex: "fidelioBusinessDate", key: "fidelioBusinessDate", width: 150 },
    { title: "Transaction ID", dataIndex: "transactionId", key: "transactionId", width: 160 },
    { title: "Excursion Code", dataIndex: "excNumber", key: "excNumber", width: 120 },
    { title: "Excursion Name", dataIndex: "excName", key: "excName", width: 160 },
    { title: "Cabin Number", dataIndex: "cabinNumber", key: "cabinNumber", width: 120 },
    { title: "Guest Name", dataIndex: "guestName", key: "guestName", width: 160 },
    { title: "Acc ID", dataIndex: "accId", key: "accId", width: 100 },
    { title: "Adult", dataIndex: "adultTicket", key: "adultTicket", width: 80 },
    { title: "Child", dataIndex: "childTicket", key: "childTicket", width: 80 },
    { title: "Infant", dataIndex: "infantTicket", key: "infantTicket", width: 80 },
    {
      title: "Total Price",
      dataIndex: "totalPrice",
      key: "totalPrice",
      width: 110,
      render: (val) => formatAmount(val),
    },
    {
      title: "Transaction Date",
      dataIndex: "transactionAt",
      key: "transactionAt",
      width: 150,
      render: (val) => (val ? moment(val).format("DD-MM-YYYY HH:mm") : "-"),
    },
    {
      title: "Status",
      dataIndex: "transactionStatus",
      key: "transactionStatus",
      width: 110,
      render: (val, row) => {
        const displayStatus = row.voided ? "voided" : val;
        return row.voided && row.voidReason ? (
          <Tooltip title={row.voidReason} enterDelay={0} enterTouchDelay={0} arrow>
            <span style={statusChipStyle(displayStatus)}>{formatStatus(displayStatus)}</span>
          </Tooltip>
        ) : (
          <span style={statusChipStyle(displayStatus)}>{formatStatus(displayStatus)}</span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 140,
      render: (val, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {!row.voided &&
            VOIDABLE_STATUSES.includes(row.transactionStatus) &&
            currentBusinessDate &&
            moment(row.fidelioBusinessDate).format("YYYY-MM-DD") === currentBusinessDate && (
            <Button
              variant="contained"
              size="small"
              style={{ background: "var(--red)", fontWeight: 500 }}
              onClick={() => handleVoid(row)}
            >
              Void
            </Button>
          )}
          <button
            className="btn delete_btn"
            title="View"
            onClick={() => navigate(`/shore/back-office/shore-excursions/report/view/${row.id}`)}
          >
            <i className="ri-eye-line"></i>
          </button>
        </div>
      ),
    },
  ];

  const OTHER_REASON_VALUE = "__other__";

  const handleVoid = async (row) => {
    const reasonOptionsHtml = voidReasons
      .map((r) => `<option value="${r}">${r}</option>`)
      .concat(`<option value="${OTHER_REASON_VALUE}">Other (please specify)</option>`)
      .join("");

    const { value: reason } = await Swal.fire({
      title: "Void this transaction?",
      icon: "warning",
      iconColor: "var(--red, #d7182a)",
      width: 440,
      customClass: { popup: "void-reason-popup" },
      html: `
        <style>
          .void-reason-popup { border-radius: 16px; padding-bottom: 28px; }
          .void-reason-popup .swal2-title { font-size: 20px; padding-top: 4px; }
          .void-reason-wrap { text-align: left; }
          .void-reason-text { color: #767676; font-size: 14px; line-height: 1.5; margin: 0 0 18px; }
          .void-reason-label {
            display: block; font-size: 11px; font-weight: 700; color: #8a8a8a;
            text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 6px;
          }
          .void-reason-select, .void-reason-textarea {
            width: 100%; box-sizing: border-box; padding: 11px 14px; border-radius: 10px;
            border: 1px solid #e0e0e0; font-size: 14px; color: #222; background: #fafafa;
            transition: border-color .15s ease, box-shadow .15s ease, background .15s ease;
            font-family: inherit;
          }
          .void-reason-select:focus, .void-reason-textarea:focus {
            outline: none; background: #fff;
            border-color: var(--red, #d7182a);
            box-shadow: 0 0 0 3px rgba(215, 24, 42, 0.12);
          }
          .void-reason-textarea { min-height: 88px; resize: vertical; margin-top: 16px; }
        </style>
        <div class="void-reason-wrap">
          <p class="void-reason-text">This cannot be undone - a voided transaction can never be changed again.</p>
          <label class="void-reason-label" for="void-reason-select">Reason</label>
          <select id="void-reason-select" class="void-reason-select">
            <option value="" selected disabled>Select a reason</option>
            ${reasonOptionsHtml}
          </select>
          <textarea id="void-reason-other" class="void-reason-textarea" style="display:none;" placeholder="Enter reason for voiding this transaction"></textarea>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Void Transaction",
      cancelButtonText: "Cancel",
      confirmButtonColor: "var(--red, #d7182a)",
      cancelButtonColor: "#9e9e9e",
      reverseButtons: true,
      focusConfirm: false,
      didOpen: () => {
        const select = document.getElementById("void-reason-select");
        const textarea = document.getElementById("void-reason-other");
        select.addEventListener("change", () => {
          textarea.style.display = select.value === OTHER_REASON_VALUE ? "block" : "none";
        });
      },
      preConfirm: () => {
        const select = document.getElementById("void-reason-select");
        const textarea = document.getElementById("void-reason-other");
        if (!select.value) {
          Swal.showValidationMessage("Please select a reason");
          return false;
        }
        if (select.value === OTHER_REASON_VALUE) {
          if (!textarea.value.trim()) {
            Swal.showValidationMessage("Reason is required");
            return false;
          }
          return textarea.value.trim();
        }
        return select.value;
      },
    });
    if (!reason) return;

    dispatch({ type: "SET_LOADING", loading: true });
    const res = await updateShorexBookingStatus(row.id, "voided", reason);
    if (res?.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: res.error, type: "error" } });
    } else {
      addlog(
        LOG_ACTIONS.VOIDED,
        LOG_TYPES.SHOREX_TRANSACTION,
        {
          title: `Transaction ${row.transactionId}`,
          transactionId: row.transactionId,
          cabinNumber: row.cabinNumber,
          guestName: row.guestName,
          amount: row.totalPrice,
          reason,
        },
        userDetails.id
      );
      await getData(page);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  const getData = async (value) => {
    dispatch({ type: "SET_LOADING", loading: true });
    const args = { page: value || page, limit: pageSize };
    if (debouncedSearch) args.search = debouncedSearch;
    if (statuses.length) args.status = statuses.join(",");
    if (excIds.length) args.excIds = excIds.join(",");
    try {
      const [fromDate, toDate] = JSON.parse(dateRange || "[]");
      if (fromDate && toDate) {
        args.fromDate = fromDate;
        args.toDate = toDate;
      }
    } catch (e) {
      // no date range selected
    }
    let data = await getShorexSalesReport(args);
    if (data.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: data.error, type: "error" } });
    } else {
      setRows(data.data || []);
      setCount(data.totalCount || 0);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  useEffect(() => {
    getData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, statuses, dateRange, excIds]);

  return (
    <Layout type={"read_per"} module_data={"Void Transaction"}>
      <CustomHeaderTitle title={"Shore Excursion Void Transaction"} />
      <div>
        <HeaderBread navigation={navigation} title={"Void Transaction"} />
      </div>
      <section className="content" style={{ marginTop: "20px" }}>
        <div className="container-fluid">
          <SwitchBar
            tabs={[
              { title: "Void Transaction", value: "void" },
              { title: "History Logs", value: "logs" },
            ]}
            activeTab={topTab}
            setActiveTab={setTopTab}
          />

          {topTab !== "void" ? (
            <Logs
              type={LOG_TYPES.SHOREX_TRANSACTION}
              actionFilter={LOG_ACTIONS.VOIDED}
              extendedColors
            />
          ) : (
          <div className="card">
            <div className="card-header two_item" style={{ alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <Select
                  multiple
                  style={{ width: "200px", height: "40px" }}
                  displayEmpty
                  value={statuses}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setStatuses(newValue.includes("__ALL__") ? [] : newValue);
                    setPage(1);
                  }}
                  renderValue={(selected) =>
                    selected.length ? selected.map(formatStatus).join(", ") : "All Status"
                  }
                >
                  <MenuItem value="__ALL__">
                    <Checkbox checked={statuses.length === 0} />
                    <ListItemText primary="All" />
                  </MenuItem>
                  {["pending", "booked", "voided", "failed"].map((statusOption) => (
                    <MenuItem key={statusOption} value={statusOption}>
                      <Checkbox checked={statuses.includes(statusOption)} />
                      <ListItemText primary={formatStatus(statusOption)} />
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  multiple
                  style={{ width: "220px", height: "40px" }}
                  displayEmpty
                  value={excIds}
                  onChange={(e) => {
                    setExcIds(e.target.value);
                    setPage(1);
                  }}
                  renderValue={(selected) =>
                    selected.length
                      ? excOptions
                          .filter((exc) => selected.includes(String(exc.excId)))
                          .map((exc) => excursionOptionLabel(exc))
                          .join(", ")
                      : "Filter by Excursion"
                  }
                >
                  {excOptions.map((exc) => (
                    <MenuItem key={exc.excId} value={String(exc.excId)}>
                      <Checkbox checked={excIds.includes(String(exc.excId))} />
                      <ListItemText
                        primary={`${exc.excNo}-${exc.name}`}
                        secondary={excursionOptionDate(exc)}
                      />
                    </MenuItem>
                  ))}
                </Select>
                <RangePicker
                  style={{ width: "250px" }}
                  onChange={(date, dateString) => {
                    setDateRange(dateString[0] && dateString[1] ? JSON.stringify(dateString) : "");
                    setPage(1);
                  }}
                  value={parseDateRange(dateRange)}
                  format={"YYYY-MM-DD"}
                />
                <Button
                  variant="contained"
                  style={{ background: "var(--red)", fontWeight: "500" }}
                  onClick={() => {
                    setStatuses([]);
                    setDateRange(getDefaultDateRange());
                    setSearch("");
                    setExcIds([]);
                    setPage(1);
                  }}
                >
                  Reset Filter
                </Button>
              </div>
              <SearchField
                value={search}
                SearchValue={() => setPage(1)}
                setValue={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="card-body">
              <div className="table_holder">
                <CustomizedTable
                  setRows={setRows}
                  setPageSize={setPageSize}
                  setPage={setPage}
                  pageSize={pageSize}
                  page={page}
                  count={count}
                  columns={columns}
                  rows={rows}
                  title={"Void Transaction"}
                />
              </div>
            </div>
          </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default ShorexVoidTransaction;
